import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './DataProducts.module.scss';
import { DataVendor, DataProduct } from 'v2/models';
import { useInfiniteQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import DataProductsList from './DataProductsList';
import AddAssociationButton from 'v2/common/DRM/AddAssociationButton/AddAssociationButton';
import NoAssociationsPlaceholder from 'v2/common/DRM/NoAssociationsPlaceholder/NoAssociationsPlaceholder';
import noDataProductsImage from 'v2/images/noDataProductsImage.svg';
import CreateDataProductModal from 'v2/common/DRM/DataProduct/CreateDataProductModal/CreateDataProductModal';
import DataProductsDetail from './DataProductDetail';
import { Spinner } from 'react-bootstrap';
import {
  DataVendorContext,
  DataVendorContextValue,
} from 'v2/utils/dataVendorUtils';
import { PAGE_SIZE } from 'utils/constants';
import { TranslationKeys, PostProcessKeys } from 'translations';
import { useTranslation } from 'react-i18next';

export interface DataProductQueryResult {
  count: number;
  results: DataProduct[];
}

interface OwnProps {
  dataVendor: DataVendor;
  refetchDataVendor?: () => void;
  className?: string;
}

type DataProductsProps = OwnProps;

const DataProductsContainer = ({
  dataVendor,
  refetchDataVendor,
  className,
}: DataProductsProps) => {
  const { t } = useTranslation();
  const [showCreateDataProductModal, setShowCreateDataProductModal] =
    useState(false);
  const [dataProductSelected, setDataProductSelected] =
    useState<DataProduct | null>(null);
  const [initialFetchComplete, setInitialFetchComplete] = useState(false);

  const { data, isFetching, fetchNextPage, hasNextPage, refetch, status } =
    useInfiniteQuery(
      ['DataVendorDetails_DataProductList'],
      async ({ pageParam = 1 }) => {
        const res = await restFactory.get<DataProductQueryResult>(
          `/dj/api/v1/data_vendor/${dataVendor.id}/data_products/`,
          {
            page: pageParam,
            size: PAGE_SIZE,
          }
        );

        return {
          ...res,
          prev: pageParam,
          next: pageParam + 1,
        };
      },
      {
        cacheTime: 0,
        refetchOnWindowFocus: false,
        getNextPageParam: (lastPage) => {
          const hasMorePages = PAGE_SIZE * lastPage.prev < lastPage.count;
          return hasMorePages ? lastPage.next : undefined;
        },
        onSuccess: (data) => {
          const productsCount = data.pages[0].count;
          if (!dataProductSelected && productsCount > 0) {
            setDataProductSelected(data.pages[0].results[0]);
          }
        },
      }
    );

  useEffect(() => {
    if (status === 'success' && !initialFetchComplete) {
      setInitialFetchComplete(true);
    }
  }, [status, initialFetchComplete]);

  const contextValue: DataVendorContextValue = {
    dataVendor,
    refetch,
    refetchDataVendor,
  };

  if (!initialFetchComplete) {
    return (
      <div className="loadingIndicatorContainer">
        <Spinner animation="border" />
      </div>
    );
  }

  const handleOnClickDataProductModal = () => {
    setShowCreateDataProductModal(true);
  };

  const handleCloseDataProductModal = () => {
    setShowCreateDataProductModal(false);
    setDataProductSelected(null);
    refetch();
  };

  const handleDeleteDataProduct = (id: number) => {
    if (id === dataProductSelected?.id) {
      setDataProductSelected(null);
    }
    refetch();
  };

  return (
    <>
      <DataVendorContext.Provider value={contextValue}>
        <div
          className={classnames(
            'container-xl',
            'h-100',
            styles.dataVendorDataProductsContainer,
            className
          )}
        >
          <div
            className={classnames(
              'd-flex fix-bg-images pt-4',
              styles.leftContent
            )}
          >
            {!data || data.pages[0]?.count === 0 ? (
              <NoAssociationsPlaceholder
                onButtonClick={() => handleOnClickDataProductModal()}
                buttonLabel={`Add New ${t(TranslationKeys.dataProduct_one, {
                  postProcess: PostProcessKeys.TitleCase,
                })}`}
                image={noDataProductsImage}
                fitContentBtn={true}
              />
            ) : (
              <>
                <AddAssociationButton
                  onClick={() => handleOnClickDataProductModal()}
                  label={`Add New ${t(TranslationKeys.dataProduct_one, {
                    postProcess: PostProcessKeys.TitleCase,
                  })}`}
                  fitContentBtn={true}
                />
                <DataProductsList
                  data={data}
                  infiniteQueryFetch={{
                    isFetching,
                    fetchNextPage,
                    hasNextPage,
                  }}
                  selected={dataProductSelected}
                  onSelect={(item) => setDataProductSelected(item)}
                  onChange={refetch}
                  onDelete={handleDeleteDataProduct}
                />
              </>
            )}
          </div>
          <DataProductsDetail
            dataProduct={dataProductSelected}
            dataVendor={dataVendor}
            onUpdate={refetch}
          />
        </div>
      </DataVendorContext.Provider>
      <CreateDataProductModal
        dataVendor={dataVendor}
        show={showCreateDataProductModal}
        onClose={handleCloseDataProductModal}
        onSuccess={() => refetch}
      />
    </>
  );
};

export default DataProductsContainer;
